import { useCallback, useEffect, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { useListData } from 'react-stately';

import type { RenderTagFn } from '@/lib/base/forms/select/multi-select';
import {
  MultiSelect,
  useTagKeyDown,
} from '@/lib/base/forms/select/multi-select';
import type { SelectItemType } from '@/lib/base/forms/select/select';
import { TagCloseX } from '@/lib/base/tags/tag-close-x';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import type { PropertyInputProps } from './TextInput';

const stopPointerDown = (e: React.PointerEvent) => {
  e.stopPropagation();
};

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

interface OptionTagProps {
  item: SelectItemType;
  onRemove: (id: Key) => void;
}

/**
 * One selected-option chip.
 *
 * `MultiSelect`'s default tag renderer always draws an `<Avatar>`, which is
 * right for its original consumers (people, org members, records) and wrong
 * here: a competitor or a status is not a person, and the fallback glyph reads
 * as one. Mirrors the entity-type chip in the CSV import step, which overrides
 * the same renderer for the same reason.
 *
 * A component (not just a plain render function) so it can call
 * `useTagKeyDown` — the same keyboard removal + adjacent-tag focus management
 * the default `MultiSelect` tag renderer uses. `renderOptionTag` below is
 * invoked once per chip from inside `MultiSelect`'s own render loop, so the
 * hook must live in its own component instance per chip rather than being
 * called directly from that loop (a variable number of hook calls within one
 * render violates the Rules of Hooks).
 */
function OptionTag({ item, onRemove }: OptionTagProps) {
  const handleKeyDown = useTagKeyDown(item.id);
  const handlePress = useCallback(() => onRemove(item.id), [onRemove, item.id]);

  return (
    <span className='flex items-center gap-1 rounded-md bg-primary py-0.5 pr-1 pl-1.5 ring-1 ring-primary ring-inset'>
      <span className='truncate text-sm font-medium text-secondary select-none'>
        {item.label}
      </span>
      <span onPointerDown={stopPointerDown}>
        <TagCloseX
          size='md'
          className='ml-0.5'
          onKeyDown={handleKeyDown}
          onPress={handlePress}
        />
      </span>
    </span>
  );
}

const renderOptionTag: RenderTagFn = (item, onRemove) => (
  <OptionTag key={item.id} item={item} onRemove={onRemove} />
);

/**
 * Render a multi-value `select` property as ONE dropdown the user can pick
 * several options from, with the chosen values shown as removable chips.
 *
 * Why this exists as its own input rather than going through the generic
 * multi-value path: `is_multi` properties normally render as stacked rows of
 * the single-value editor (`InlineMultiValueAdapter`), which renders each
 * row's add/remove controls as a JSX sibling of the row's editor.
 * `SelectInput` renders a `<Select>` trigger and has no trailing slot to put
 * them in, so for `select` those controls were dropped and a second value was
 * unreachable — the field looked and behaved single-valued no matter what the
 * schema said. A chip picker is also what a multi-select field is expected to
 * be, so this replaces the stacked rows for `select` rather than patching them.
 *
 * `MultiSelect` owns its selection state as a react-stately `ListData`, while
 * `PropertyInput` is fully controlled (`value` in, `onChange` out). The bridge
 * below — seed from `value`, reconcile on external change, emit on
 * insert/clear — mirrors `AssociatedCrmObjectInput`, which solves the same
 * impedance mismatch for association pickers.
 */
export const MultiSelectInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  size = 'sm',
}) => {
  const items = useMemo<SelectItemType[]>(
    () =>
      (propertyType.options ?? []).map(opt => ({
        id: String(opt.value),
        label: opt.label,
      })),
    [propertyType.options]
  );

  // The committed values, normalised to option ids. A multi-value property
  // holds an array, but tolerate a bare scalar: a field flipped from
  // single- to multi-select keeps its pre-flip value until it is next written.
  // Deduped: `ListData` (used for `selectedItems` below) is keyed by id, so a
  // duplicate id in the stored value would make its item count permanently
  // differ from the deduped `nextIds` the reconcile effect below compares
  // against — the effect would then churn a remove/re-append on every render
  // without ever converging.
  const selectedValues = useMemo<string[]>(() => {
    if (value === null || value === undefined || value === '') {
      return [];
    }
    const raw = Array.isArray(value) ? value : [value];
    const ids = raw.filter(v => v !== null && v !== undefined).map(String);
    return Array.from(new Set(ids));
  }, [value]);

  // `items` is the schema-derived pick-list of selectable options.
  // `resolvedItems` additionally synthesizes a `{ id, label: id }` entry for
  // any selected id whose option was deleted from the schema, so it still
  // renders as a chip with its raw id as the label. This is deliberately
  // asymmetric: an orphaned id must render as an existing chip but must NOT
  // reappear as a pickable option in the dropdown, so `items` itself (passed
  // as MultiSelect's `items` prop below) is left untouched — don't
  // "simplify" these into one list. Used for BOTH places selected chips are
  // computed (the ListData seed below and the reconcile effect's nextItems)
  // so the two paths can't silently diverge.
  const resolvedItems = useMemo<SelectItemType[]>(() => {
    const itemIds = new Set(items.map(item => item.id));
    const orphaned = selectedValues
      .filter(id => !itemIds.has(id))
      .map(id => ({ id, label: id }));
    return orphaned.length > 0 ? [...items, ...orphaned] : items;
  }, [items, selectedValues]);

  const selectedItems = useListData<SelectItemType>({
    initialItems: resolvedItems.filter(item =>
      selectedValues.includes(item.id)
    ),
    getKey: item => item.id,
  });

  // Reconcile the ListData when `value` changes from OUTSIDE this component
  // (a websocket update, a form reset, a parent overwrite). Selections made
  // here already left MultiSelect's own state correct before `onChange` fired,
  // so those land as a no-op rather than a remove/append churn.
  useEffect(() => {
    const currentIds = selectedItems.items.map(item => item.id);
    const nextIds = [...selectedValues].sort();
    if (arraysEqual(nextIds, [...currentIds].sort())) {
      return;
    }
    if (currentIds.length > 0) {
      selectedItems.remove(...currentIds);
    }
    const nextItems = resolvedItems.filter(item =>
      selectedValues.includes(item.id)
    );
    if (nextItems.length > 0) {
      selectedItems.append(...nextItems);
    }
    // `selectedItems` is a fresh object every render, so depending on it would
    // re-run this on every render; it is read, not reacted to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues, resolvedItems]);

  const handleItemInserted = useCallback(
    (key: Key) => {
      const id = String(key);
      if (selectedValues.includes(id)) {
        return;
      }
      onChange([...selectedValues, id]);
    },
    [onChange, selectedValues]
  );

  const handleItemCleared = useCallback(
    (key: Key) => {
      const id = String(key);
      onChange(selectedValues.filter(existing => existing !== id));
    },
    [onChange, selectedValues]
  );

  const renderItem = useCallback(
    (item: SelectItemType) => (
      <MultiSelect.Item id={item.id} textValue={item.label}>
        {item.label}
      </MultiSelect.Item>
    ),
    []
  );

  const Icon = getPropertyTypeIconComponent(propertyType);

  return (
    <MultiSelect
      // Keep the prompt visible even once chips exist. The association picker
      // blanks it, but the search input is a flex sibling of the chips with a
      // `min-w-[20%]` floor, so at some widths it wraps to its own line — and
      // a blank placeholder makes that line read as unexplained empty space
      // rather than as the field it is. The label is customer-written text
      // (a custom property's name), so it is rendered exactly as they typed it.
      placeholder={placeholder || `Select ${propertyType.label}`}
      renderTag={renderOptionTag}
      size={size}
      items={items}
      selectedItems={selectedItems}
      inputRef={inputRef}
      onItemInserted={handleItemInserted}
      onItemCleared={handleItemCleared}
      isDisabled={disabled}
      shortcut={false}
      aria-label={propertyType.label}
      placeholderIcon={Icon}
    >
      {renderItem}
    </MultiSelect>
  );
};
